import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AdminPanelPage } from '../AdminPanelPage'
import { BreadcrumbProps, Button, FilterPanel, Icon, InlineEditableText, Input, Modal, Tooltip, useToast, DashboardGrid, DashboardGridItem, DashboardGridLayout } from '@aidkitorg/component-library'
import { DashboardBuilderPanel } from './components/DashboardBuilderPanel'
import { useLocalizedStrings } from '../../Localization'
import { useDashboards } from '../../Contexts/DashboardsContext'
import { DashboardComponentCard } from './components/DashboardComponents/DashboardComponentCard'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useAsyncEffect } from '../../Hooks/AsyncEffect'
import { DashboardFilterProvider, useDashboardFilter } from './contexts/DashboardFilterContext'
import { useDashboardFilterPanel } from './hooks/useDashboardFilterPanel'
import { useSettings } from '../../Contexts/SettingsContext'
import { FilterConfig } from '@aidkitorg/types/lib/admin-panel/filters'
import { ParentFieldEntry } from '@aidkitorg/types/lib/admin-panel/settings'
import { BooleanExpr } from '@aidkitorg/types/lib/survey'
import { convertFilterGroupToBooleanExpr, convertBooleanExprToFilterGroup } from '../Reports/utils/filterConverters'

const DashboardBuilderPageInner = ({instanceId}: {instanceId: string}) => {
  const L = useLocalizedStrings();
  const { dashboardId } = useParams<{ dashboardId?: string }>()
  const location = useLocation()

  const newDashboard = location.pathname === '/admin-panel/dashboards/new'
  const isEditMode = Boolean(dashboardId)

  const { 
    dashboardsById,
    stagedComponents, 
    setStagedComponents, 
    createDashboard, 
    updateDashboard,
    fetchDashboardById 
  } = useDashboards()
  const { setAvailableFields, setFilterConfig } = useDashboardFilter()
  const { filterPanelProps, filterRef } = useDashboardFilterPanel(instanceId)
  const { fields, isFieldsLoading } = useSettings()
  const { toast } = useToast()
  const history = useHistory()

  const [originalTitle, setOriginalTitle] = useState<string>('My Dashboard')
  const [title, setTitle] = useState<string>('My Dashboard')
  const [description, setDescription] = useState<string>('')
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(isEditMode)

  const titleRef = useRef<HTMLInputElement>(null)
  const descriptionRef = useRef<HTMLInputElement>(null);
  const titleEditor = (
    <Input
      ref={titleRef}
      label={L.admin_panel.dashboards.title}
      showLabel={false}
      textVariant="h2"
      defaultValue={isLoadingDashboard ? '' : title}
      required={true}
    />
  );
  const descriptionEditor = (
    <Input
      ref={descriptionRef}
      label={L.admin_panel.dashboards.dashboard_description}
      showLabel={false}
      textVariant="p"
      placeholder={L.admin_panel.dashboards.dashboard_desc_placeholder}
      defaultValue={isLoadingDashboard ? '' : description}
    />
  );

  const getCurrentValues = () => {
    const titleInput = titleRef.current?.value
    const descriptionInput = descriptionRef.current?.value

    if (titleInput) setTitle(titleInput)
    if (descriptionInput) setDescription(descriptionInput)

    return {
      currentTitle: titleInput ?? title,
      currentDescription: descriptionInput ?? description
    }
  }

  const [gridItems, setGridItems] = useState<Array<DashboardGridItem<any>>>([])

  const GRID_ROW_HEIGHT = 160
  const GRID_COLS = 6
  const GRID_WIDTH = 1200

  const sizeByKind: Record<string, { w: number, h: number, minW: number, minH: number }> = {
    'Hero Number':    { w: 1, h: 1, minW: 1, minH: 1 },
    'Bar Chart':      { w: 4, h: 3, minW: 2, minH: 3 },
    'Pie Chart':      { w: 4, h: 3, minW: 2, minH: 3 },
    'Map':            { w: 5, h: 4, minW: 2, minH: 3 },
    'Summary Table':  { w: 4, h: 3, minW: 2, minH: 2 },
    'Report':         { w: 5, h: 3, minW: 5, minH: 3 }
  };

  const getSizeForKind = (kind: string) => sizeByKind[kind] || { w: 2, h: 2 };

  // Transform staged components into DashboardGridItem format
  useEffect(() => {
    const mappedGridItems = stagedComponents.map((component) => ({
      id: component.id,
      data: component,
      layout: ("style" in component && component.style) ? component.style : undefined
    }))
    setGridItems(mappedGridItems)
  }, [stagedComponents])

  const [currentLayout, setCurrentLayout] = useState<DashboardGridLayout[]>([]);

  // Available fields for filters (all fields that user has access to)
  const availableFieldsForFilters = useMemo(() => {
    return fields.map(field => ({
      key: field.id,
      label: field.displayName || field.id,
      type: !field.parent ? ((field as ParentFieldEntry).dataType ?? 'text') : 'text'
    }));
  }, [fields]);

  // Update context with available fields when they change
  useEffect(() => {
    setAvailableFields(availableFieldsForFilters)
  }, [availableFieldsForFilters, setAvailableFields])

  // Extract common filters from loaded dashboard components
  const extractFiltersFromComponents = useCallback((components: any[]) => {
    if (!components || components.length === 0) return undefined;

    // Get filters from all components that have them
    const componentFilters = components
      .map(comp => comp.config?.filter)
      .filter(filter => filter != null);

    if (componentFilters.length === 0) return undefined;

    // For simplicity, we'll take the first filter found
    // In a more sophisticated implementation, you might want to find common filters
    // or handle cases where components have different filters
    const firstFilter = componentFilters[0];

    try {
      // Convert BooleanExpr back to FilterConfig format
      const filterGroup = convertBooleanExprToFilterGroup(firstFilter, availableFieldsForFilters);
      if (filterGroup) {
        return {
          enabled: true,
          config: filterGroup
        } as FilterConfig;
      }
    } catch (error) {
      console.warn('Failed to convert component filter to FilterConfig:', error);
    }

    return undefined;
  }, [availableFieldsForFilters]);

  // Handle filter changes by updating staged components with filter
  const handleFilterChange = useCallback((newFilterConfig: FilterConfig) => {
    const hasComplete = newFilterConfig && newFilterConfig.enabled && newFilterConfig.config;
    
    // Convert FilterConfig to BooleanExpr for component configs
    let booleanExpr: BooleanExpr | undefined = undefined;
    if (hasComplete) {
      try {
        booleanExpr = convertFilterGroupToBooleanExpr(newFilterConfig);
      } catch (error) {
        console.warn('Failed to convert filter config to boolean expression:', error);
      }
    }

    // Update all staged components with the new filter in their config
    setStagedComponents(prevComponents => 
      prevComponents.map(component => ({
        ...component,
        config: {
          ...component.config,
          filter: booleanExpr  // Add filter to component config
        }
      }))
    );
  }, [setStagedComponents])

  const getDefaultSize = (item: DashboardGridItem<any>) => {
    return getSizeForKind(item.data.config.kind);
  };

  const renderGridItem = (item: DashboardGridItem<any>, dimensions: { width: number; height: number }) => (
    <DashboardComponentCard 
      id={item.data.id}
      isEditMode={isEditMode || newDashboard}
      component={item.data.config}
      width={dimensions.width}
      height={dimensions.height}
    />
  );
  
  useAsyncEffect(async () => {
    if (isEditMode && dashboardId) {
      const loadExistingDashboard = async () => {
        setIsLoadingDashboard(true)
        try {
          await fetchDashboardById(dashboardId)
        } catch (e) {
          // TODO: notify user of error while loading dashboard
          history.push('/admin-panel/dashboards')
          setIsLoadingDashboard(false)
        }
      }
      await loadExistingDashboard()
    }
  }, [isEditMode, dashboardId])

  useEffect(() => {
    if (isEditMode && dashboardId && dashboardsById[dashboardId] && isLoadingDashboard) {
      const dashboard = dashboardsById[dashboardId]
      setTitle(dashboard.title)
      setOriginalTitle(dashboard.title)
      setDescription(dashboard.description || description)
      setStagedComponents(dashboard.components)
      
      // Extract and set filters from components
      const extractedFilters = extractFiltersFromComponents(dashboard.components);
      if (extractedFilters) {
        setFilterConfig(extractedFilters);
      }
      
      setIsLoadingDashboard(false)
    }  
  }, [isEditMode, dashboardsById, isLoadingDashboard, extractFiltersFromComponents, setFilterConfig])

  useEffect(() => {
    return () => {
      setStagedComponents([]); // Clear components when unmounting
    };
  }, []);

  const createDashboardHandler = useCallback(async () => {
    const { currentTitle, currentDescription } = getCurrentValues()
    const createdDashboard = await createDashboard(currentTitle, currentDescription, stagedComponents, currentLayout)
    if (createdDashboard) {
      setStagedComponents([])
      toast({
        title: L.admin_panel.dashboards.success,
        description: L.admin_panel.dashboards.dashboard_created,
        variant: 'success'
      })
        
      history.push("/admin-panel/dashboards/"+ createdDashboard)
    } else {
      toast({
        title: L.admin_panel.dashboards.error,
        description: L.admin_panel.dashboards.error_creating,
        variant: 'error'
      })
    }
  }, [getCurrentValues, stagedComponents, currentLayout, createDashboard])

  const createDashboardButton = useMemo(() => (
    <Button variant="primary" onClick={createDashboardHandler}>
      {L.admin_panel.dashboards.create_dashboard}
    </Button>
  ), [createDashboardHandler])


  const saveDashboardButton = useMemo(() => (
    <Button variant="primary" onClick={async () => {
      const { currentTitle, currentDescription } = getCurrentValues()

      if (dashboardId) {
        const updatedDashboard = await updateDashboard(dashboardId, currentTitle, currentDescription, stagedComponents, currentLayout)
        if (updatedDashboard) {
          setStagedComponents([])
          toast({
            title: L.admin_panel.dashboards.success,
            description: L.admin_panel.dashboards.dashboard_saved,
            variant: 'success'
          })
          
          await fetchDashboardById(dashboardId, true)
        
          history.push("/admin-panel/dashboards/"+ updatedDashboard)
        } else {
          toast({
            title: L.admin_panel.dashboards.error,
            description: L.admin_panel.dashboards.error_saving,
            variant: 'error'
          })
        }
      }
    }}>
      {L.admin_panel.dashboards.save_dashboard}
    </Button>
  ), [dashboardId, getCurrentValues, stagedComponents, currentLayout])

  const pageActionButtons = [
    isEditMode ? saveDashboardButton : createDashboardButton
  ]
  
  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.dashboards,
      href: '/admin-panel/dashboards'
    },
    {
      label: isEditMode ? originalTitle : L.admin_panel.dashboards.dashboard_builder,
    }
  ]

  const gridEmptyState = (        
    <div className="flex-grow flex items-center justify-center h-64 text-gray-500">
      <div className="text-center">
        <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z" />
        </svg>
        <div className="text-lg font-medium mb-2">{L.admin_panel.dashboards.build_your_dashboard}</div>
        <div>{L.admin_panel.dashboards.build_your_dashboard_msg}</div>
      </div>
    </div>
  )

  const updateComponentStyles = (layout: Array<DashboardGridLayout>) => {
    const updatedComponents = stagedComponents.map(component => {
      const matchingLayout = layout.find(item => item.i === component.id);

      if (matchingLayout) {
        return {
          ...component,
          style: matchingLayout,
        };
      }

      return component;
    });

    setStagedComponents(updatedComponents);
  };

  const GridArea = () => {
    return (
      <div className="flex-grow">
        <DashboardGrid
          items={gridItems}
          renderItem={renderGridItem}
          rowHeight={GRID_ROW_HEIGHT}
          isDraggable={true}
          isResizable={true}
          draggableHandle=".drag-handle"
          onLayoutChange={(layout: Array<DashboardGridLayout>) => {
            updateComponentStyles(layout)
            setCurrentLayout(layout)
          }}
          getDefaultSize={getDefaultSize}
          defaultGridCols={GRID_COLS}
          defaultGridWidth={GRID_WIDTH}
          emptyState={gridEmptyState}
        />
      </div>
    );
  }

  return (
    <AdminPanelPage
      title={titleEditor}
      description={descriptionEditor}
      pageActionButtons={pageActionButtons}
      breadcrumbs={breadcrumbs}
      keyboardShortcuts={[
        { key: 'B', description: L.admin_panel.reports.toggle_sidebar, modifier: 'meta' },
        { key: 'K', description: L.admin_panel.command_palette.keyboard_shortcut, modifier: 'meta' },
        { key: 'S', description: L.admin_panel.dashboards.save_dashboard, modifier: 'meta', callback: () => { createDashboardHandler() } },
      ]}
    >
      <div className="space-y-4 w-full h-full">
        <FilterPanel 
          {...filterPanelProps}
          onConfirm={handleFilterChange}
        />
        <div className="flex justify-between gap-4 w-full">
          { GridArea() }
          <div className="flex-shrink-0 w-80 h-full border rounded-md bg-white">
            <DashboardBuilderPanel/>
          </div>
        </div>
      </div>
    </AdminPanelPage>
  )
}

export const DashboardBuilderPage = () => {
  const { dashboardId } = useParams<{ dashboardId?: string }>()
  
  // Define instance ID for filter context
  const instanceId = `dashboard-builder-${dashboardId || 'new'}`;

  return (
    <DashboardFilterProvider enableUrlFilters={false}>
      <DashboardBuilderPageInner instanceId={instanceId} />
    </DashboardFilterProvider>
  )
}